<template>
  <b-form class="px-2 mb-3">
    <b-row>
      <b-col lg="3">
        <b-form-group
          label="Basic Details"
          label-class="product-creation-section-labels py-0"
        >
          <small class="sub-section-labels">Enter product details</small>
        </b-form-group>
      </b-col>
      <b-col lg="5">
        <b-form-group
          label-class="product-creation-labels"
          :state="!formValidatedState('name')"
          :invalid-feedback="apiErrors.name"
        >
          <template slot="label">
            Product Name
            <span class="font-weight-bold text-danger">*</span>
          </template>
          <b-form-input
            id="productName"
            :maxlength="maxLengths.productName"
            v-model="basic_details.name"
          >
          </b-form-input>
        </b-form-group>
        <b-form-group
          label-class="product-creation-labels"
          :state="!formValidatedState('description')"
          :invalid-feedback="apiErrors.description"
        >
          <template slot="label">
            Description <span class="text-danger font-weight-bold">*</span>
          </template>
          <b-form-textarea
            name="description"
            id="description"
            class="form-control"
            :rows="3"
            :maxlength="maxLengths.description"
            v-model="basic_details.description"
          >
          </b-form-textarea>
          <div class="counter-container mt-1 text-right">
            {{ descriptionCount }} / {{ maxLengths.description }}
          </div>
        </b-form-group>
        <b-form-group
          v-if="isSimpleProduct"
          label-class="product-creation-labels"
          :state="!formValidatedState('regular_price')"
          :invalid-feedback="apiErrors.regular_price"
        >
          <template slot="label"
            >Price <span class="font-weight-bold text-danger">*</span></template
          >
          <b-input-group
            class="price-input-group form-control p-0 overflow-hidden"
          >
            <template #prepend>
              <b-input-group-text class="bg-transparent border-0">
                ₱
              </b-input-group-text>
            </template>
            <b-form-input
              id="price"
              v-model="basic_details.regular_price"
              required
              type="number"
              min="1"
              max="10000000000"
              class="border-0"
              @keyup="handleInput()"
            />
          </b-input-group>
        </b-form-group>
        <b-form-group
          label="Product Link"
          label-class="product-creation-labels"
          class="d-none"
        >
          <b-input-group class="form-control p-0 overflow-hidden">
            <template #prepend>
              <b-input-group-text class="bg-transparent border-0">
                <i class="bi bi-link" aria-hidden="true"></i>
              </b-input-group-text>
            </template>
            <b-form-input
              id="product-link"
              class="border-0"
              v-model="basic_details.product_link"
            >
            </b-form-input>
          </b-input-group>
        </b-form-group>
        <b-form-group
          v-if="isSimpleProduct"
          label-class="product-creation-labels"
          :state="!formValidatedState('sku')"
          :invalid-feedback="apiErrors.sku"
        >
          <template slot="label">
            SKU
            <span class="text-danger font-weight-bold" v-if="isSkuRequired"
              >*</span
            >
          </template>
          <b-form-input
            id="sku"
            v-model="basic_details.sku"
            @keyup.native="trimSpaces($event)"
          >
          </b-form-input>
        </b-form-group>
        <BrandBranches
          v-if="branches.length > 1"
          @applyBrandsAndBranches="applyBrandsAndBranches"
          errorKey="branch_uuids"
          :brandAndBranchUuids="brandAndBranchUuids"
          formGroupLabel="Tag to a store branch"
          formGroupClass="secondary-label mb-2 product-creation-labels"
          isDropRight
          :isAllSelected="
            origin === 'update_product'
              ? basic_details.selected_all_brands_branches
              : false
          "
          :bypassMaxWidth="true"
        />
        <b-form-group label-class="product-creation-labels"
          v-show="origin != 'update_product'">
          <template slot="label">
            Visibility
            <switches
              v-model="basic_details.visibility"
              theme="custom"
              color="primary"
              type-bold="true"
              style="top: 23px"
              :emit-on-mount="false"
              class="ml-2"
            />
          </template>
        </b-form-group>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { mapState } from "vuex";
import BrandBranches from "@/components/store/BrandBranches";
import Switches from "vue-switches";
import { productsMixins } from "@/mixins/products_mixins";

export default {
  props: ["origin"],
  components: { BrandBranches, Switches },
  data() {
    return {
      maxLengths: {
        productName: 40,
        description: 2500,
      },
    };
  },
  computed: {
    ...mapFields("products", ["basic_details", "product_variables"]),
    ...mapState("products", ["errors"]),
    ...mapState("branches", ["branches"]),
    ...mapState("estoreInventories", {
      estoreErrors: (state) => state.errors,
    }),
    descriptionCount: {
      get() {
        return this.basic_details.description
          ? this.basic_details.description.length
          : 0;
      },
      set(count) {
        return count;
      },
    },
    isSkuRequired() {
      return this.product_variables.length === 0;
    },
    isSimpleProduct() {
      const { type } = this.$route.params;

      if (type) return type === "simple";
      else return this.product_variables.length === 0;
    },
    brandAndBranchUuids: {
      get() {
        const selectedBranches = this.branches.filter(({ uuid }) =>
          this.basic_details.branch_uuids.includes(uuid)
        );
        return this.basic_details.branch_uuids.length > 0
          ? selectedBranches.map(({ uuid, brand_uuid }) => ({
              branch_uuid: uuid,
              brand_uuid,
            }))
          : [];
      },
      set(values) {
        return values;
      },
    },
  },
  mixins: [productsMixins],
  async created() {
    await this.$store.dispatch("branches/getBranches", true);
  },
  methods: {
    applyBrandsAndBranches(brandAndBranches, isAllStores) {
      if (isAllStores) {
        this.basic_details.selected_all_brands_branches = true;
      } else {
        this.basic_details.selected_all_brands_branches = false;
        this.basic_details.branch_uuids = brandAndBranches.map(
          ({ branch_uuid }) => branch_uuid
        );
      }
    },
    handleInput(){
      const regex = /^\d+(\.\d{0,2})?$/;
      if(!regex.test(this.basic_details.regular_price)){
        this.basic_details.regular_price = parseFloat(this.basic_details.regular_price).toFixed(2)
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.bi {
  font-size: 18px !important;
  &.bi-link {
    top: -1px;
  }
}
</style>
